import { twMerge } from 'tailwind-merge'
import React, { useState } from 'react'
import { useDrag } from 'react-dnd'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { useSelector } from 'react-redux'
import structureTypes from 'common/constants/structureTypes'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { EntityUnionType } from 'common/types/entities/EntityInterface'
import copyToClipboard from 'common/utils/copyToClipboard'
import BlueButton from 'client/components/core/BlueButton'
import { Dialog, DialogBody, DialogHeader } from 'client/components/core/Dialog'
import { getBlockFile } from 'client/reducers/blocksReducer'
import { BlockType } from './types'
import { Files } from 'client/store/files/filesReducer'

// FYI: will be refactored in this ticket: https://app.shortcut.com/systeme/story/55255/redesign-blocks-listing

const demoImage =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAV4AAACWBAMAAABkyf1EAAAAG1BMVEXh5eZte4t7iJaKlaGnsLjS19rEys+Yoq21vcMcLj6CAAAACXBIWXMAAA7EAAAOxAGVKw4bAAADBUlEQVR4nO2YTW/aQBCGJwGTHj0LITnaadJeMf3KMSZSe8WpqvaI00Ov0CrtFQ7N7+7MGuMFKuEDbBzpfSTwxrMjP2z2Y4AIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGgcR8xs3o5r9Az44DI1UF/mVzV6NsTXXFzEzJPdPRvieypvP/hqd88G+VLa292zSb4nNVya5BvwfGfPJvlSMpW3W76U9yxsxy/HneS1bnLByPxe9lTftVhnaK410Em68yxc5XvxXQyI7mRjk3WXncp+cZXaNuVyb+74urGZxCSN5M+e+i7zvfjmIQUxX3BXnLrdx5nhj7oGA+Yh9x1fJ9bm7i+W6wvmhNMq34uvjM8JP9BX2Ygznkq7TyeGqCWDmxvH14kdd8d0J37HZkL3XOV78ZVLdibXeEqZuHTEqyN++bm255WvEzvq69jKVDrVe+Eq35evPpcW8txzdZvIa0xpJPeSQeXrxD4PyHrPNByHq3xfvjN1y2Sc+2R9SF7JjTpEla8TU9pyL5lLQ+Zvme/FV+ZvYiufc/tIu9eKU6wueVj5OjGib58SaccTsuNa5nvxzSOKS9+wcrLLZzlmxf7r+I60PxV95DPFPn3TwfJ5vdq+LTYfHrd8axQi+/CViZpExR3XaWs+OLFFb2zbZZ8y34evrvkk3PbdWm8bMW0XfcJVvg/fluyrqbbur9acUlV197ONuaL7b9EnXOX78E1lmdj9Xv6prtPWeeHOFfE91jOl75wXB58U1vc7R3r23sh5erOxpjbOY3c+vKNA97OWnMe5+Jb5B/Ytvr+NbW3zhs2601a946438yc542sZWzNkOd/K/AP7Vt+P82U96e6xm/Xk2tgzT2OZBAtmo+dw7qWe1N8frguhkW2s+QY/zQP935duzXu6vyz66J63zH8WLKKnNqjNFy0l0uiJLeqT2nry0HXv/sht3T5/ao3aHJm/nZTr/GLYDNo+yrJ9ooX685m+MnmHq99UAAAAAAAAAAAAAAAAAAAAAAAAAABA4/kHG6uDwenboGUAAAAASUVORK5CYII'

interface BlockProps {
  block: BlockType
  remove: () => void
  isRemoveAllowed?: boolean
  isMobile?: boolean
  isSharingAllowed?: boolean
  isMasterBlockInUse?: boolean
  addToTheBottom: (entities: EntityUnionType[]) => void
}

interface BlocksState {
  userBlocks: BlockType[]
  masterBlocks: BlockType[]
  commonBlocks: BlockType[]
  userFiles: Files
  masterFiles: Files
  commonFiles: Files
}

export const Block = ({
  block,
  remove,
  isRemoveAllowed,
  isMobile,
  isSharingAllowed = false,
  isMasterBlockInUse = false,
  addToTheBottom,
}: BlockProps) => {
  const { t } = useTypedTranslation()
  const [isShareDialogOpen, setShareDialogOpen] = useState(false)
  const desktopFile = useSelector((state: { blocks: BlocksState }) =>
    getBlockFile(state.blocks, block.desktop_preview?.id),
  )
  const mobileFile = useSelector((state: { blocks: BlocksState }) =>
    getBlockFile(state.blocks, block.mobile_preview?.id),
  )
  const hasSection = Boolean(
    block.content.find(
      ent =>
        ent.type === structureTypes.SECTION ||
        ent.type === EntityTypeEnum.Section,
    ),
  )
  const [, drag] = useDrag({
    item: {
      id: block.id,
      entities: block.content,
      type: 'BLOCK',
      isMaster: Boolean(block.isMaster),
    },
    canDrag: !isMasterBlockInUse,
  })

  const safeRemove = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.stopPropagation()
    if (
      window.confirm(
        t(
          block.isMaster
            ? 'components.core.blocks.confirm_remove_master'
            : 'components.core.blocks.confirm_remove',
        ),
      )
    ) {
      remove()
    }
  }

  const title =
    (isMasterBlockInUse &&
      t('components.core.blocks.master_block.disabled_message')) ||
    ''

  return (
    <>
      <div
        ref={drag}
        aria-disabled={isMasterBlockInUse}
        onClick={() => !isMasterBlockInUse && addToTheBottom(block.content)}
        title={title}
        className={twMerge(
          'relative mt-10 mr-0.5 ml-0.5 cursor-pointer border-gray-100 border-b pb-5 opacity-100 last:border-0 [&:first-of-type]:mt-2.5',
          isMasterBlockInUse && 'cursor-not-allowed opacity-50',
        )}
      >
        <div
          className={twMerge(
            'flex h-auto min-h-[120px] w-full items-center justify-center [&>img]:max-h-full [&>img]:max-w-full',
            isMobile && '[&>img]:max-h-[300px] [&>img]:max-w-[150px]',
          )}
        >
          <img
            alt={title}
            src={
              isMobile && mobileFile
                ? mobileFile.path
                : desktopFile
                  ? desktopFile.path
                  : demoImage
            }
          />
        </div>
        <div className=" flex w-full items-center justify-between p-2">
          <span className="mr-auto text-M">{block.title}</span>
          <div className="flex gap-3.5">
            {isSharingAllowed && (
              <button
                type="button"
                className="cursor-pointer px-1"
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  setShareDialogOpen(true)
                }}
              >
                <i className="fa-regular fa-share-nodes text-S text-iconSecondary" />
              </button>
            )}
            {isRemoveAllowed && (
              <button
                type="button"
                className="cursor-pointer px-1"
                onClick={safeRemove}
              >
                <i className="fa-regular fa-trash text-S text-iconSecondary" />
              </button>
            )}
          </div>
        </div>
      </div>
      {isShareDialogOpen && (
        <BlockShareDialog
          block={block}
          close={() => setShareDialogOpen(false)}
        />
      )}
    </>
  )
}

interface BlockShareDialogProps {
  block: BlockType
  close: () => void
}

function BlockShareDialog({ block, close }: BlockShareDialogProps) {
  const { t } = useTypedTranslation()
  const [isCopied, setCopied] = useState(false)
  const shareUrl = `${process.env.SYSTEME_IO_HOST}/dashboard/share?hash=${block.hash}&type=${
    block.isMaster ? 'master-block' : 'user-block'
  }`

  const handleCopy = () => {
    copyToClipboard(shareUrl)
    setCopied(true)
    setTimeout(close, 1500)
  }

  return (
    <Dialog show close={close} small>
      <DialogHeader close={close} center grayHeader={false}>
        <h2>{t('components.core.blocks_library.share_dialog.title')}</h2>
      </DialogHeader>
      <DialogBody small center>
        <p>{t('components.core.blocks_library.share_dialog.description')}</p>
        <div className="my-4 rounded-sm border border-border200 border-solid bg-iconTertiary p-2 text-S">
          {shareUrl}
        </div>
        <BlueButton onClick={handleCopy}>
          {t('components.core.blocks_library.share_dialog.copy_button.text')}
        </BlueButton>
        {isCopied && (
          <p>
            <small className="text-iconSuccess">
              {t('components.core.blocks_library.share_dialog.is_copied')}
            </small>
          </p>
        )}
      </DialogBody>
    </Dialog>
  )
}
